import pluralize from "pluralize";
import {useFormContext} from "react-hook-form";
import {FiChevronRight} from "react-icons/fi";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TagsField} from "@/components/form/fields/TagsField/TagsField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {MatchModeToggle} from "@/components/matchModeToggle/MatchModeToggle.tsx";
import {Section} from "@/components/section/Section.tsx";
import {METRICS_EXPLORER} from "@/lib/featureFlags.ts";
import {ServiceTag} from "@/lib/types/checks.ts";
import {DashboardFormFields} from "@/pages/dashboard/edit/dashboardForm.ts";
import {DashboardFormCharts} from "@/pages/dashboard/edit/subforms/DashboardFormCharts.tsx";
import {CheckSortOptionValues} from "@/pages/dashboard/edit/subforms/dashboardFormChecks.types.ts";

const checkSortOptions = Object.values(CheckSortOptionValues);

interface DashboardFormContentProps {
  tags: ServiceTag[];
  onOpenCheckPicker: () => void;
}

export const DashboardFormContent = ({tags, onOpenCheckPicker}: DashboardFormContentProps) => {
  const {watch, setValue} = useFormContext<DashboardFormFields>();
  const {userHasFeature} = useCurrentUserContext();

  const selectedCheckIds = watch("services_selected");
  return (
    <div className="d-flex flex-column gap-4">
      {/* Metrics Summary */}
      <div className="d-flex flex-column gap-3">
        <Section title={"Metrics Summary"} description={"Summary metrics displayed at the top of the dashboard"} />
        <Card className="bg-white d-flex flex-column gap-4">
          <SwitchField
            labelPosition="left"
            name="metrics_show_section"
            label="Show Metrics Summary section"
            description="Display metrics on Checks, Outages, Response Time etc."
          />
        </Card>
      </div>

      {/* Checks to Display */}
      <div className="d-flex flex-column gap-3">
        <Section title={"Checks to Display"} description={"Summary metrics displayed at the top of the dashboard"} />
        <Card className="bg-white d-flex flex-column gap-4">
          <SwitchField
            labelPosition="left"
            name="metrics_for_all_checks"
            label="Include all Checks"
            labelTypography={"bodySmall"}
            description="Display metrics from all Checks, not just the selected Checks"
          />
          {!watch("metrics_for_all_checks") && (
            <>
              <Divider />
              <div className="d-flex flex-column gap-3 ps-3">
                <div
                  role="button"
                  tabIndex={0}
                  className="d-flex align-items-center justify-content-between cursor-pointer"
                  onClick={onOpenCheckPicker}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onOpenCheckPicker();
                  }}
                >
                  <div className="d-flex flex-column gap-1">
                    <span className="pulseTextBodySmall">Manually Select Checks</span>
                    <span className="pulseTextBodySmall pulse-color-gray-600">
                      {pluralize("Check", selectedCheckIds.length, true)} selected
                    </span>
                  </div>
                  <Icon icon={FiChevronRight} className="pulse-color-gray-600" />
                </div>
              </div>
            </>
          )}
          <Divider />
          <div className="d-flex flex-column gap-4 pb-2">
            <TagsField
              name="services_tags"
              label="Require matching tags"
              helpText="Narrows your selection to only checks that match these tags"
              tags={tags}
              value={watch("services_tags").map((tag) => tag.id)}
              onChange={(tagIds) =>
                setValue(
                  "services_tags",
                  tagIds.map((id) => ({id}))
                )
              }
              className={"d-flex flex-column"}
            />
            <MatchModeToggle
              value={watch("match_all_tags") ? "AND" : "OR"}
              onChange={(mode) => setValue("match_all_tags", mode === "AND")}
            />
          </div>
        </Card>
      </div>

      {/* Check Cards */}
      <div className="d-flex flex-column gap-3">
        <Section title={"Check Cards"} description={"Individual check cards showing status and performance"} />
        <Card className="bg-white d-flex flex-column gap-4">
          <SwitchField labelPosition="left" name="services_show_section" label="Show Check cards" />
          {watch("services_show_section") && (
            <>
              <Divider />
              <div className="d-flex flex-column gap-3 ps-3">
                <span className="pulseTextBodySmallBold">Include checks in state</span>
                <SwitchField labelPosition="left" name="services_include_up" label="Up" labelTypography={"bodySmall"} />
                <SwitchField
                  labelPosition="left"
                  name="services_include_down"
                  label="Down"
                  labelTypography={"bodySmall"}
                />
                <SwitchField
                  labelPosition="left"
                  name="services_include_paused"
                  label="Paused"
                  labelTypography={"bodySmall"}
                />
                <SwitchField
                  labelPosition="left"
                  name="services_include_maintenance"
                  label="Maintenance"
                  labelTypography={"bodySmall"}
                />
              </div>
              <Divider />
              <div className="d-flex gap-4 ps-3">
                <div className="w-50">
                  <SelectField
                    name="services_num_to_show"
                    label="Min cards to display"
                    helpText="Fewer may be displayed depending on settings or available checks"
                    options={[4, 8, 12, 16, 20, 24].map((i) => ({label: i.toString(), value: i}))}
                  />
                </div>
                <div className="d-flex flex-column gap-3 w-50">
                  <span className="pulseTextBodySmallBold">Display on cards</span>
                  <SwitchField
                    labelPosition="left"
                    name="services_show_uptime"
                    label="Uptime (24h)"
                    labelTypography={"bodySmall"}
                  />
                  <SwitchField
                    labelPosition="left"
                    name="services_show_response_time"
                    label="Response Time (24h)"
                    labelTypography={"bodySmall"}
                  />
                </div>
              </div>
              <Divider />
              <div className="d-flex gap-4 ps-3 pb-2">
                <div className="w-50">
                  <SelectField name="services_primary_sort" label="Primary sort order" options={checkSortOptions} />
                </div>
                <div className="w-50">
                  <SelectField
                    name="services_secondary_sort"
                    label="Secondary sort order"
                    tooltip="The secondary value for ordering Checks, when the primary value is the same."
                    options={checkSortOptions}
                  />
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {userHasFeature(METRICS_EXPLORER) && <DashboardFormCharts />}

      {/* Alerts */}
      <div className="d-flex flex-column gap-3">
        <Section title={"Alerts"} description={"Recent alert activity displayed at the bottom of the dashboard"} />
        <Card className="bg-white d-flex flex-column gap-4 mb-3 pb-4">
          <SwitchField labelPosition="left" name="alerts_show_section" label="Show Latest Alerts" />
          {watch("alerts_show_section") && (
            <>
              <Divider />
              <div className="d-flex flex-column gap-3 ps-3">
                <SwitchField
                  labelPosition="left"
                  name="alerts_for_all_checks"
                  label="Show Alerts for all Checks"
                  labelTypography={"bodySmall"}
                />
                <Divider />
                <SwitchField
                  labelPosition="left"
                  name="alerts_include_ignored"
                  label="Include Ignored Alerts"
                  labelTypography={"bodySmall"}
                />
                <Divider />
                <SwitchField
                  labelPosition="left"
                  name="alerts_include_resolved"
                  label="Include Resolved Alerts"
                  labelTypography={"bodySmall"}
                />
                <Divider />
                <SelectField
                  name="alerts_num_to_show"
                  label="Alerts to display"
                  helpText="Fewer may be displayed depending on available alerts"
                  options={[5, 10, 15].map((i) => ({label: i.toString(), value: i}))}
                />
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
